import {
  CASE_OUTCOMES,
  CASE_TYPES,
  type CaseOutcome,
  type CaseRow,
  type CaseType,
} from '@contract';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';
import { OpenRecord } from '@/components/queue/queue-parts';
import { Cell, StatusBadge } from '@/components/record-table';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { callAdmin } from '@/lib/api';
import { invalidate } from '@/lib/query';
import { useCan } from '@/lib/staff-context';

export const CASE_TYPE_LABEL: Record<CaseType, string> = {
  debited_not_paid: 'Money left the wallet but the payment failed',
  sent_not_received: 'Sent, but the recipient did not get it',
  bill_not_credited: 'Bill or airtime paid but not credited',
  wrong_recipient: 'Sent to the wrong person',
  not_made_by_customer: 'A payment the customer did not make',
  fee_dispute: 'A fee the customer disputes',
};

export const CASE_OUTCOME_LABEL: Record<CaseOutcome, string> = {
  no_fault: 'No fault found',
  refunded: 'Refunded',
  reversed: 'Reversed',
  recovered_from_provider: 'Recovered from the provider',
  written_off: 'Written off',
};

/** Opens a case for a customer, optionally about a payment or a report. */
export function OpenCase({
  customerId,
  transactionId,
  issueId,
}: Readonly<{
  customerId: string;
  transactionId?: string | null;
  issueId?: string;
}>) {
  const [open, setOpen] = useState(false);
  if (!useCan('cases.open')) return null;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Open a case
      </Button>
      {open ? (
        <OpenCaseDialog
          customerId={customerId}
          transactionId={transactionId ?? undefined}
          issueId={issueId}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function OpenCaseDialog({
  customerId,
  transactionId,
  issueId,
  onClose,
}: Readonly<{
  customerId: string;
  transactionId?: string;
  issueId?: string;
  onClose: () => void;
}>) {
  const navigate = useNavigate();
  const [type, setType] = useState<CaseType>('debited_not_paid');
  const [summary, setSummary] = useState('');
  const valid = summary.trim().length >= 8 && summary.trim().length <= 500;
  const open = useMutation({
    mutationFn: () =>
      callAdmin('cases.open', {
        customer_id: customerId,
        type,
        summary: summary.trim(),
        transaction_id: transactionId,
        issue_id: issueId,
      }),
    onSuccess: async (row) => {
      await invalidate('cases.list');
      onClose();
      void navigate({ to: '/cases/$caseId', params: { caseId: row.id } });
    },
  });

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Open a case</DialogTitle>
          <DialogDescription>
            A case gathers the payments, reports, provider claims and fixes
            about one problem until it is closed with an outcome.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Select
            label="Type"
            value={type}
            onChange={(event) => setType(event.target.value as CaseType)}
          >
            {CASE_TYPES.map((key) => (
              <option key={key} value={key}>
                {CASE_TYPE_LABEL[key]}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="case-summary">What happened</Label>
          <Textarea
            id="case-summary"
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">8 to 500 characters.</p>
        </div>
        {open.error ? (
          <Alert variant="destructive">{open.error.message}</Alert>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!valid || open.isPending}
            onClick={() => open.mutate()}
          >
            Open the case
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Closes a case with an outcome and its reason (§9.7). The outcome has no
 * default. A refund or reversal outcome with no applied fix on the case is
 * warned about; closing is still allowed.
 */
export function CloseCaseDialog({
  caseId,
  hasAppliedFix,
  open,
  onOpenChange,
}: Readonly<{
  caseId: string;
  hasAppliedFix: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}>) {
  const [outcome, setOutcome] = useState<CaseOutcome | ''>('');
  const [reason, setReason] = useState('');
  const valid =
    outcome !== '' && reason.trim().length >= 8 && reason.trim().length <= 500;
  const close = useMutation({
    mutationFn: () =>
      callAdmin('cases.close', {
        case_id: caseId,
        outcome: outcome as CaseOutcome,
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      await invalidate('cases.get', 'cases.list');
      onOpenChange(false);
    },
  });
  const unbacked =
    (outcome === 'refunded' || outcome === 'reversed') && !hasAppliedFix;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !close.isPending && onOpenChange(next)}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Close the case</DialogTitle>
          <DialogDescription>
            Every outcome needs its reason. A closed case does not reopen.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Select
            label="Outcome"
            value={outcome}
            placeholder="Choose an outcome"
            onChange={(event) =>
              setOutcome(event.target.value as CaseOutcome | '')
            }
          >
            {CASE_OUTCOMES.map((key) => (
              <option key={key} value={key}>
                {CASE_OUTCOME_LABEL[key]}
              </option>
            ))}
          </Select>
        </div>
        {unbacked ? (
          <Alert variant="warning">
            No applied fix is linked to this case. You can still close it.
          </Alert>
        ) : null}
        <div className="flex flex-col gap-2">
          <Label htmlFor="case-reason">Reason</Label>
          <Textarea
            id="case-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">8 to 500 characters.</p>
        </div>
        {close.error ? (
          <Alert variant="destructive">{close.error.message}</Alert>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!valid || close.isPending}
            onClick={() => close.mutate()}
          >
            Close the case
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const column = createColumnHelper<CaseRow>();

/** The columns of a list of cases on a customer's page. A row opens the case sheet. */
export const CASE_COLUMNS = [
  column.accessor('id', {
    header: 'Case',
    cell: (info) => (
      <OpenRecord
        sheetRef={{ type: 'case', id: info.getValue() }}
        label={`Open case ${info.getValue().slice(0, 8)}`}
        className="font-mono text-xs"
      >
        {info.getValue().slice(0, 8)}
      </OpenRecord>
    ),
  }),
  column.accessor('opened_at', {
    header: 'Opened',
    cell: (info) => <Cell kind="datetime" value={info.getValue()} />,
  }),
  column.accessor('type', {
    header: 'Type',
    cell: (info) => CASE_TYPE_LABEL[info.getValue()],
  }),
  column.accessor('customer', {
    header: 'Customer',
    cell: (info) => <Cell kind="customer" value={info.getValue()} />,
  }),
  column.accessor('owner', {
    header: 'Owner',
    cell: (info) => <Cell kind="staff" value={info.getValue()} />,
  }),
  column.accessor('status', {
    header: 'Status',
    cell: (info) => <StatusBadge status={info.getValue()} />,
  }),
  column.accessor('open_claims', { header: 'Open claims' }),
];
